import { defineComponent } from "vue";
import { UploadFile } from "./InvoiceUploader";
import Utils from "../utils/Utils";

let debug = false;
let ix = 1;

const PhotoUploaderImage = defineComponent({
    template: `
        <img v-if="blob" class="PhotoUploaderImage" :src="imgurl" />
    `,

    props: {
        file: { type: Object, required: true },
    },

    data() {
        return {
            blob: null,
        };
    },

    async created() {
        const up: UploadFile = this.file;
        this.blob = new Blob([await up.file.arrayBuffer()]);
    },

    computed: {
        imgurl() {
            return URL.createObjectURL(this.blob);
        },
    },
});

const PhotoUploaderImages = defineComponent({
    components: {
        PhotoUploaderImage,
    },

    template: `
        <div class="PhotoUploaderImages" :class="{ 'PhotoUploaderImages--edit': edit }">
            <div v-if="!edit" class="PhotoUploaderImages__preview" @click="onEdit">
                <PhotoUploaderImage :file="last" />

                <div class="PhotoUploaderImages__count">{{ photos.length }}</div>
            </div>

            <div v-if="edit" class="PhotoUploaderImages__list">
                <div class="PhotoUploaderImages__items">
                    <div v-for="file in photos" class="PhotoUploaderImages__item">
                        <PhotoUploaderImage :file="file" />

                        <button type="button" class="PhotoUploaderImages__remove" @click="onRemove(file)">
                            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
                                <path fill-rule="evenodd" clip-rule="evenodd" d="M0.768485 0.768485C1.12647 0.410505 1.70687 0.410505 2.06485 0.768485L6 4.70364L9.93515 0.768485C10.2931 0.410505 10.8735 0.410505 11.2315 0.768485C11.5895 1.12647 11.5895 1.70687 11.2315 2.06485L7.29636 6L11.2315 9.93515C11.5895 10.2931 11.5895 10.8735 11.2315 11.2315C10.8735 11.5895 10.2931 11.5895 9.93515 11.2315L6 7.29636L2.06485 11.2315C1.70687 11.5895 1.12647 11.5895 0.768485 11.2315C0.410505 10.8735 0.410505 10.2931 0.768485 9.93515L4.70364 6L0.768485 2.06485C0.410505 1.70687 0.410505 1.12647 0.768485 0.768485Z" fill="white"/>
                            </svg>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    `,

    emits: [
        'remove',
        'change:edit',
    ],

    props: {
        files: { type: Array, required: true },
    },

    data() {
        return {
            edit: false,
        };
    },

    computed: {
        last() {
            return this.photos[this.photos.length - 1];
        },

        photos() {
            return this.files.filter((v: UploadFile) => v.photo);
        },
    },

    methods: {
        onEdit() {
            this.edit = true;

            this.$emit('change:edit', true);
        },

        onRemove(file: UploadFile) {
            this.edit = false;

            this.$emit('remove', file);
            this.$emit('change:edit', false);
        },
    },
});

export default defineComponent({
    components: {
        PhotoUploaderImages,
    },

    template: `
        <Teleport to="body">
            <div class="PhotoUploader" :class="{ 'PhotoUploader--inited': inited != null }">
                <div class="PhotoUploader__inner">
                    <button type="button" class="PhotoUploader__close" @click="onClose"><i class="fa fa-chevron-left" /></button>

                    <div class="PhotoUploader__camera">
                        <video ref="video"></video>
                        <canvas ref="canvas"></canvas>

                        <div class="PhotoUploader__camera-overlay"></div>
                    </div>

                    <div class="info">{{ info }}</div>

                    <div class="PhotoUploader__footer">
                        <div class="PhotoUploader__footer-buttons">
                            <button v-if="!images.length && !edit" type="button" class="PhotoUploader__upload" @click="onUpload" :disabled="true"><i class="fa fa-upload" /></button>

                            <PhotoUploaderImages
                                v-if="images.length"

                                :files="images"

                                @change:edit="edit = $event"
                                @remove="$emit('image:remove', $event)"
                            />

                            <button v-if="!edit" type="button" class="PhotoUploader__photo" @click="onPhoto"><span></span></button>

                            <button v-if="!edit" type="button" class="PhotoUploader__accept" @click="onAccept" :disabled="!images.length"><i class="fa fa-check" /></button>
                        </div>
                    </div>
                </div>
            </div>
        </Teleport>
    `,

    emits: [
        'image:add',
        'image:remove',
        'close',
        'accept',
    ],

    props: {
        images: { type: Array, required: true },
    },

    mounted() {
        this.startVideo();
    },

    data() {
        return {
            inited: false,
            ms: null,
            video: null,
            canvas: null,
            ctx: null,

            timer: null,

            info: null,

            edit: false,
        };
    },

    methods: {
        getPaperRect(w: number, h: number) {
            const f = w / h;

            const pf = 21 / 29.7;

            let r = {
                x: null,
                y: null,
                w: null,
                h: null,
            };

            if (f > pf) {
                r.y = 0;
                r.h = h;

                const ww = Math.round(h * pf);

                r.x = Math.round((w - ww) / 2);
                r.w = ww;
            } else {
                r.x = 0;
                r.w = w;

                const hh = Math.round(w / pf);

                r.y = Math.round((h - hh) / 2);
                r.h = hh;
            }

            return r;
        },

        getCroppedFrame(w: number, h: number): OffscreenCanvas {
            const video: HTMLVideoElement = this.video;

            const vw = video.videoWidth;
            const vh = video.videoHeight;

            const f = w / h;
            const vf = vw / vh;

            const r = {
                x: null,
                y: null,
                w: null,
                h: null,
            };

            let cx, cy;

            if (f > vf) {
                r.x = 0;
                r.w = vw;

                cx = vw;
                cy = Math.round(vw / f);

                r.h = vh;
                r.y = Math.round((cy - vh) / 2);
            } else {
                r.y = 0;
                r.h = vh;

                cx = Math.round(vh * f);
                cy = vh;

                r.w = vw;
                r.x = Math.round((cx - vw) / 2);
            }

            const c = new OffscreenCanvas(cx, cy);
            const ctx = c.getContext('2d');

            ctx.drawImage(video, r.x, r.y, r.w, r.h);

            return c;
        },

        getPaperFrame(): OffscreenCanvas {
            const canvas: HTMLCanvasElement = this.canvas;
            const cvid: OffscreenCanvas = this.getCroppedFrame(canvas.width, canvas.height);
            const r = this.getPaperRect(cvid.width, cvid.height);

            const result: OffscreenCanvas = new OffscreenCanvas(r.w, r.h);
            const ctx = result.getContext('2d');

            ctx.drawImage(cvid, r.x, r.y, r.w, r.h, 0, 0, result.width, result.height);

            return result;
        },

        async drawFrame() {
            const canvas: HTMLCanvasElement = this.canvas;

            canvas.width = canvas.clientWidth * 2;
            canvas.height = canvas.clientHeight * 2;

            const cvid: OffscreenCanvas = this.getCroppedFrame(canvas.width, canvas.height);

            const r = this.getPaperRect(cvid.width, cvid.height);

            const cvtx = cvid.getContext("2d");
            const pp = 8;
            cvtx.strokeStyle = '#fff';
            cvtx.lineWidth = pp;
            cvtx.strokeRect(r.x + pp / 2, r.y + pp / 2, r.w - pp, r.h - pp);

            const ctx: CanvasRenderingContext2D = this.ctx;
            ctx.drawImage(cvid, 0, 0, canvas.width, canvas.height);
        },

        async fetchImage() {
            const frame: OffscreenCanvas = this.getPaperFrame();

            return frame.convertToBlob({
                type: 'image/jpeg',
            });
        },

        async startVideo() {
            try {
                const r = await navigator.mediaDevices.getUserMedia({
                    audio: false,
                    video: {
                        width: {
                            ideal: 2560,
                        },
                        height: {
                            ideal: 1440,
                        },
                        facingMode: {
                            ideal: 'environment',
                        }
                    },
                });

                this.ms = r;
                this.inited = true;

                const video: HTMLVideoElement = this.$refs.video;
                this.video = video;
                video.srcObject = r;
                video.play();

                video.addEventListener('canplay', () => {
                    const canvas: HTMLCanvasElement = this.$refs.canvas;
                    this.canvas = canvas;

                    const ctx = canvas.getContext('2d');
                    this.ctx = ctx;

                    if (!this.timer) {
                        this.timer = setInterval(() => {
                            this.drawFrame();
                        }, 1000 / 30);
                    }
                });
            } catch (e) {
                if (debug) {
                    this.inited = true;
                    return;
                }

                alert('Należy zezwolić na korzystanie z kamery');

                this.$emit('close');
            }
        },

        async stopVideo() {
            const ms: MediaStream = this.ms;

            if (this.timer) {
                clearInterval(this.timer);
                this.timer = null;
            }

            if (ms) {
                const video: HTMLVideoElement = this.$refs.video;
                video.srcObject = null;

                for (const track of ms.getTracks()) {
                    track.stop();
                }

                this.ms = null;
                this.video = null;
                this.canvas = null;
                this.ctx = null;
            }
        },

        async onClose() {
            await this.stopVideo();

            this.$emit('close');
        },

        async onUpload() {

        },

        async onPhoto() {
            const blob = await this.fetchImage();

            const fix = ix;

            const file = new File([blob], 'zdjecie' + fix + '.jpg', {
                type: 'image/jpeg',
            });

            const up: UploadFile = {
                id: Utils.genUid(),
                name: file.name,
                file: file,
                blob: blob,

                photo: true,
            };

            ix = ix + 1;

            this.$emit('image:add', up);
        },

        async onAccept() {
            this.onClose();

            this.$emit('accept');
        },
    },
});
